import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Check, Loader2, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import SectionBand from '@/components/ui/SectionBand';
import ActionModalShell from '@/components/ui/ActionModalShell';
import { supabase } from '@/lib/supabase';
import { getApiUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { COMPARE_ROWS, FayeLogo } from '@/components/checkout/FayeInsuranceOffer';
import {
  FAYE_PRODUCT_SUMMARY_DISCLOSURE,
  FAYE_RETAILER_DISCLOSURE,
  FAYE_TRIP_COVERAGE_DISCLOSURE,
} from '../../../supabase/functions/_shared/faye-pricing.js';
import { FAYE_SUPPORT } from '../../../supabase/functions/_shared/trip-insurance.js';

const money = (cents) => `$${(Math.round(Number(cents) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return null;
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};
const requestId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`);

async function callTripInsurance(body) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Please sign in to continue.');
  const res = await fetch(getApiUrl('bookings/trip-insurance'), {
    method: 'POST',
    headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

/** "+$35.58 / payment" or "$142.31 today", per how this trip pays it. */
function PlanPrice({ plan }) {
  const pay = plan.payment || {};
  if (pay.mode === 'upfront') {
    return (
      <span className="text-right tabular-nums whitespace-nowrap">
        <span className="block text-[14px] font-medium text-white">{money(plan.premiumCents)}</span>
        <span className="block text-[12px] font-normal text-white/50">charged today</span>
      </span>
    );
  }
  return (
    <span className="text-right tabular-nums whitespace-nowrap">
      <span className="block text-[14px] font-medium text-white">
        +{money(pay.per_payment_increase_cents)}
        <span className="text-[12px] font-normal text-white/55"> / payment</span>
      </span>
      <span className="block text-[12px] font-normal text-white/50">{money(plan.premiumCents)} total</span>
    </span>
  );
}
PlanPrice.propTypes = { plan: PropTypes.object.isRequired };

/** How the chosen plan is paid, in a sentence: exactly what each payment
 *  carries, including the few cents that don't divide evenly. */
function paidLine(plan) {
  const pay = plan?.payment || {};
  if (pay.mode === 'upfront') {
    return 'Your trip is paid off, so the plan is charged once, today, to your card on file. No fees added.';
  }
  const n = pay.payments || 0;
  const from = shortDate(pay.first_payment_date);
  const each = Number(pay.per_payment_increase_cents) || 0;
  const last = Number(pay.last_payment_increase_cents) || each;
  const firstExtra = Number(pay.first_payment_extra_cents) || 0;
  let split;
  if (n <= 1) split = `+${money(plan.premiumCents)}`;
  else if (firstExtra > 0) split = `+${money(each + firstExtra)} on the first, then +${money(each)}`;
  else if (last > each) split = `+${money(each)} each, +${money(last)} on the last`;
  else split = `+${money(each)} each`;
  return `Added across your ${n === 1 ? 'remaining payment' : `${n} remaining payments`}${from ? ` from ${from}` : ''}: ${split}. Your last payment date doesn't change, payments you've made don't change, and no fees are added.`;
}

/**
 * Add Faye travel insurance to a booked trip (Trip Details). Asks the server
 * whether this trip can add it right now and what each plan costs here
 * (supabase/functions/trip-insurance-add); renders nothing when it can't.
 *
 * Same rules as checkout's offer: describe the coverage and price, coverage
 * questions go to Faye, nothing says when the policy is bought, and "Accept"
 * unlocks only once the customer has scrolled to the end of the disclosures.
 * What differs is how it's paid: spread across the payments left, or charged
 * once today on a trip that's paid off.
 */
export default function AddTripInsuranceCard({ bookingId, onAdded }) {
  const [quote, setQuote] = useState(null);
  const [tier, setTier] = useState(null);
  const [sheet, setSheet] = useState(null); // 'compare' | 'details' | 'accept' | null
  const [readToEnd, setReadToEnd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const endRef = useRef(null);
  /** One request id per accept. A retry of the same accept (a lost response,
   *  a double tap, an answer still being confirmed) reuses it, so the server
   *  can recognize it and never charge twice; it was new on every tap. A
   *  definite answer (added, declined, a new price) ends the request. */
  const requestRef = useRef(null);

  const loadQuote = useCallback(async () => {
    try {
      const { ok, data } = await callTripInsurance({ action: 'quote', booking_id: bookingId });
      if (!ok) return setQuote(null);
      setQuote(data);
      setTier((t) => (data?.plans?.some((p) => p.tier === t) ? t : data?.plans?.[0]?.tier || null));
    } catch {
      setQuote(null);
    }
    return undefined;
  }, [bookingId]);

  useEffect(() => { if (bookingId) loadQuote(); }, [bookingId, loadQuote]);

  // "Accept" unlocks when the end of the disclosures scrolls into view.
  useEffect(() => {
    if (sheet !== 'accept' || !endRef.current || typeof IntersectionObserver === 'undefined') return undefined;
    setReadToEnd(false);
    const root = endRef.current.closest('.overflow-y-auto') || null;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setReadToEnd(true);
        observer.disconnect();
      }
    }, { root });
    observer.observe(endRef.current);
    return () => observer.disconnect();
  }, [sheet]);

  // An earlier add still being confirmed: say so rather than offer another.
  const confirming = !done && quote?.eligible && quote?.available === false && quote?.reason === 'add_pending';
  if (!done && !confirming && !(quote?.eligible && quote?.available && quote.plans?.length)) return null;

  const plans = quote?.plans || [];
  const plan = plans.find((p) => p.tier === tier) || plans[0] || null;
  const upfront = plan?.payment?.mode === 'upfront';

  const accept = async () => {
    if (!plan || busy) return;
    setBusy(true);
    setError(null);
    const key = `${plan.tier}:${plan.premiumCents}`;
    if (requestRef.current?.key !== key) requestRef.current = { key, id: requestId() };
    try {
      const { ok, data } = await callTripInsurance({
        action: 'apply',
        booking_id: bookingId,
        tier: plan.tier,
        premium_cents: plan.premiumCents,
        disclosure_version: quote.disclosure_version,
        disclosures_accepted_at: new Date().toISOString(),
        client_request_id: requestRef.current.id,
      });
      if (!ok || !data?.ok) {
        setError(data?.error || "We couldn't add travel insurance just now. Please try again.");
        // Still being confirmed (or another payment is running): keep this
        // request, so trying again can't start a second charge. Anything else
        // is a definite answer: the next try is a new request.
        if (!['payment_unconfirmed', 'add_pending', 'payment_in_progress'].includes(data?.code)) requestRef.current = null;
        // A new price, a changed schedule, an add being confirmed, or one
        // already on the trip: show the current state.
        if (['insurance_price_changed', 'schedule_changed', 'retry_with_new_request', 'payment_unconfirmed', 'add_pending', 'has_policy'].includes(data?.code)) loadQuote();
        // Insurance already on the trip (added from another tab, say): the
        // trip page shows it once it reloads.
        if (data?.code === 'has_policy') onAdded?.();
        setSheet(null);
        return;
      }
      requestRef.current = null;
      setSheet(null);
      setDone({ label: data.plan?.label || plan.label, pending: Boolean(data.pending), message: data.message || null });
      onAdded?.();
    } catch (e) {
      // No answer at all: the request may have gone through, so a retry reuses it.
      setError(e?.message || "We couldn't add travel insurance just now. Please try again.");
      setSheet(null);
    } finally {
      setBusy(false);
    }
  };

  const optionClass = (active) => cn(
    'w-full text-left rounded-xl border px-3.5 py-3 transition-all duration-200',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/60',
    active
      ? 'border-brand-600 bg-brand-600/15 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
      : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]',
  );

  return (
    <motion.div
      className="bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-900/12 backdrop-blur-sm border border-brand-800/30 rounded-3xl overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.25 }}
    >
      <SectionBand
        icon={ShieldCheck}
        iconColor="text-brand-400"
        title="Travel Insurance"
        rightSlot={<FayeLogo className="h-4 w-auto text-white/70 flex-shrink-0" />}
      />
      <div className="p-6 space-y-4">
        {confirming ? (
          <p className="text-sm text-white/70 font-normal" role="status">{quote.message}</p>
        ) : done ? (
          <div className="flex items-start gap-3" role="status">
            <span className="mt-0.5 inline-flex shrink-0 items-center justify-center w-6 h-6 rounded-full bg-emerald-500/15 ring-1 ring-inset ring-emerald-500/30">
              <Check className="w-3.5 h-3.5 text-emerald-300" strokeWidth={3} />
            </span>
            <div className="min-w-0">
              <p className="text-white text-[15px] font-medium">{done.pending ? 'Payment received' : `${done.label} added to your trip`}</p>
              <p className="mt-1 text-sm text-white/60 font-normal">
                {done.message || 'Faye issues your policy before you travel. We’ve emailed you the details.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h4 className="text-white text-[15px] sm:text-base font-medium leading-tight">Add travel insurance to your trip</h4>
                <span className="text-[10px] uppercase tracking-wider font-medium text-white/55 bg-white/[0.06] rounded-full px-2 py-0.5">Optional</span>
              </div>
              <p className="mt-1.5 text-[13px] leading-relaxed text-white/70">
                Flight delayed 3+ hours: <strong className="font-medium text-white/90">free airport lounge</strong>.
              </p>
              <p className="text-[13px] leading-relaxed text-white/70">
                6+ hours: <strong className="font-medium text-white/90">$200 sent to your phone</strong>.
              </p>
            </div>

            <div className="space-y-2.5" role="radiogroup" aria-label="Travel insurance plan">
              {plans.map((p) => {
                const active = p.tier === plan?.tier;
                return (
                  <button key={p.tier} type="button" role="radio" aria-checked={active} onClick={() => setTier(p.tier)} className={optionClass(active)}>
                    <div className="flex items-start gap-2.5">
                      <span className={cn(
                        'mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full border transition-colors',
                        active ? 'border-brand-600 bg-brand-600' : 'border-white/30',
                      )}>
                        {active && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <span className={cn('text-[14px] font-medium leading-snug', active ? 'text-white' : 'text-white/90')}>{p.label}</span>
                          <PlanPrice plan={p} />
                        </div>
                        <ul className="mt-2 space-y-1">
                          {p.highlights.map((h) => (
                            <li key={h} className="relative pl-3.5 text-[12.5px] text-white/55 before:content-[''] before:absolute before:left-0.5 before:top-[0.62em] before:w-1 before:h-1 before:rounded-full before:bg-white/30">
                              {h}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {plan && <p className="text-[12.5px] text-white/55">{paidLine(plan)}</p>}
            {error && <p className="text-[12.5px] text-rose-300/90" role="alert">{error}</p>}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <button
                type="button"
                onClick={() => setSheet('accept')}
                disabled={!plan || busy}
                className="h-10 px-5 rounded-full bg-white text-black text-sm font-semibold hover:bg-white/90 transition-colors disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Add ${plan?.label || 'travel insurance'}`}
              </button>
              <button type="button" onClick={() => setSheet('compare')} className="text-[13px] text-brand-300 underline decoration-brand-300/40 underline-offset-[3px] hover:decoration-brand-300">Compare plans</button>
              <button type="button" onClick={() => setSheet('details')} className="text-[13px] text-brand-300 underline decoration-brand-300/40 underline-offset-[3px] hover:decoration-brand-300">Details &amp; disclosures</button>
            </div>
            <p className="text-[11.5px] text-white/40">Not required for your trip. Defera isn&rsquo;t a licensed insurance agent; Faye answers coverage questions.</p>
          </>
        )}
      </div>

      <ActionModalShell
        isOpen={sheet === 'compare'}
        onClose={() => setSheet(null)}
        icon={ShieldCheck}
        title="Compare plans"
        description="Up to the amounts shown, per Faye's plan terms"
      >
        <table className="w-full text-[13px] tabular-nums">
          <thead>
            <tr className="border-b border-white/10">
              <th scope="col" className="py-2 text-left font-normal"><span className="sr-only">Benefit</span></th>
              {plans.map((p) => (
                <th key={p.tier} scope="col" className="py-2 pl-3 text-right whitespace-nowrap text-[12px] font-medium text-white/60">{p.label.replace('Faye ', '').replace(' International', ' Intl')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map(([name, key]) => (
              <tr key={key} className="border-b border-white/[0.06]">
                <td className="py-2 pr-2 text-white/60">{name}</td>
                {plans.map((p) => {
                  const v = p.compare?.[key];
                  return (
                    <td key={p.tier} className={cn('py-2 pl-3 text-right whitespace-nowrap', v === true ? 'text-emerald-300' : v === false ? 'text-white/35' : 'text-white/85')}>
                      {v === true ? 'Yes' : v === false ? 'No' : v}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!upfront && (
              <tr>
                <td className="pt-3 text-white font-medium">Added per payment</td>
                {plans.map((p) => <td key={p.tier} className="pt-3 pl-3 text-right whitespace-nowrap text-white font-medium">+{money(p.payment?.per_payment_increase_cents)}</td>)}
              </tr>
            )}
            <tr>
              <td className={cn(upfront ? 'pt-3' : 'pt-1', 'text-white font-medium')}>{upfront ? 'Charged today' : 'Total for this trip'}</td>
              {plans.map((p) => <td key={p.tier} className={cn(upfront ? 'pt-3' : 'pt-1', 'pl-3 text-right whitespace-nowrap text-white font-medium')}>{money(p.premiumCents)}</td>)}
            </tr>
          </tbody>
        </table>
        <p className="mt-3 text-[11.5px] text-white/45">Percentages are of non-refundable trip costs.</p>
      </ActionModalShell>

      <ActionModalShell
        isOpen={sheet === 'details' || sheet === 'accept'}
        onClose={() => setSheet(null)}
        icon={ShieldCheck}
        title={sheet === 'accept' && plan ? `Before you add ${plan.label}` : 'Details & disclosures'}
        description="Travel insurance by Faye"
        footer={sheet === 'accept' && plan ? (
          <div className="space-y-2">
            <p className={cn('text-center text-[12px]', readToEnd ? 'text-emerald-300/90' : 'text-white/45')}>
              {readToEnd ? 'You’ve reached the end.' : 'Scroll to the end to accept.'}
            </p>
            <button
              type="button"
              onClick={accept}
              disabled={!readToEnd || busy}
              className="w-full h-11 rounded-full bg-white text-black text-sm font-semibold transition-colors hover:bg-white/90 disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed"
            >
              {busy
                ? 'Adding…'
                : upfront
                  ? `Accept and pay ${money(plan.premiumCents)}`
                  : `Accept and add ${plan.label}`}
            </button>
          </div>
        ) : null}
      >
        <dl className="space-y-3 text-[13px]">
          <div>
            <dt className="text-[12px] font-medium text-white/55">How it&rsquo;s paid</dt>
            <dd className="text-white/85">{plan ? paidLine(plan) : ''}</dd>
          </div>
          <div>
            <dt className="text-[12px] font-medium text-white/55">When your policy is issued</dt>
            <dd className="text-white/85">
              {upfront
                ? 'Faye issues your policy before you travel, and your policy number will appear on your trip page.'
                : 'Faye issues your policy before you travel. Your policy number shows on your trip once your trip is paid in full.'}
            </dd>
          </div>
          <div><dt className="text-[12px] font-medium text-white/55">If your trip is cancelled</dt><dd className="text-white/85">The cancel screen shows what happens to the travel insurance part of what you&rsquo;ve paid; where it&rsquo;s refunded, it goes back to your card, not into Defera Credit.</dd></div>
          <div><dt className="text-[12px] font-medium text-white/55">Free look</dt><dd className="text-white/85">Once issued, you can review your policy and cancel it within 14 days if it&rsquo;s not right for you.</dd></div>
          <div><dt className="text-[12px] font-medium text-white/55">Questions about coverage</dt><dd className="text-white/85">Faye answers those: <span className="select-all">{FAYE_SUPPORT.email}</span> · <span className="select-all whitespace-nowrap">{FAYE_SUPPORT.phoneUs}</span></dd></div>
        </dl>
        {sheet === 'accept' && (
          <div className="mt-4 pt-3 border-t border-white/10 text-[12.5px] leading-relaxed text-white/75 space-y-2">
            <p className="text-white/85 font-medium">By adding it, you agree that:</p>
            <ul className="space-y-1.5 list-disc pl-4 marker:text-white/30">
              <li>The cancellation and refund rules you agreed to when you booked don&rsquo;t apply to the travel insurance part of your payments, which follows Faye&rsquo;s terms and what the cancel screen shows; any refund of it is made to your card.</li>
              {!upfront && <li>If your payment plan defaults, its unpaid travel insurance premium is part of the balance due only if your travel insurance policy has been issued.</li>}
              <li>{upfront ? `${money(plan?.premiumCents)} is charged today to your card on file.` : 'Your remaining payments change as shown above.'}</li>
            </ul>
          </div>
        )}
        <div className="mt-4 pt-3 border-t border-white/10 space-y-2 text-[11.5px] leading-relaxed text-white/50">
          <p>{FAYE_TRIP_COVERAGE_DISCLOSURE}</p>
          <p>{FAYE_RETAILER_DISCLOSURE}</p>
          <details open={sheet === 'accept'}>
            <summary className="cursor-pointer text-white/65">Full plan disclosure</summary>
            <p className="mt-2">{FAYE_PRODUCT_SUMMARY_DISCLOSURE}</p>
          </details>
        </div>
        <div ref={endRef} aria-hidden="true" className="h-px" />
      </ActionModalShell>
    </motion.div>
  );
}

AddTripInsuranceCard.propTypes = {
  bookingId: PropTypes.string.isRequired,
  onAdded: PropTypes.func,
};
